// @flow

import Vue from 'vue'
import type { Commit, Dispatch } from 'vuex'
import type { ConfigurationState } from './state'
import {
  SET_AVAILABLE_FILTER_ENTITIES, SET_COVERED_PERIOD, SET_PRODUCT_CONFIGURATIONS, SET_SELECTED_CLASS,
  SET_SELECTED_CUSTOMER_GROUP,
  SET_SELECTED_FROM_STOP, SET_SELECTED_PRODUCT, SET_SELECTED_STAR_VALUE, SET_SELECTED_TERM, SET_SELECTED_TO_STOP,
  SET_SELECTED_ZONE, SET_SESSION_ID
} from './mutation-types'
import type { SelectionState } from '../selection/state'
import { formatProductsApiDate } from '../../../utils/date'
import type { ConfigurationCriteria } from '../../../products-api/types'

type ConfigurationActionContext = { commit: Commit, dispatch: Dispatch, state: ConfigurationState, rootState: { selection: SelectionState }, rootGetters: Object, getters: Object }

// Number of called configurationFilterRequests
let configurationFilterRequestCount = 0

const buildFilterCriteria = (state: ConfigurationState, rootGetters: Object, startDate) => {
  // The customergroup selected in the overview is used here, *not* the customergroup selected in the details page
  const filteredCustomerGroups = rootGetters['selection/availableSelectedCustomerGroups'].map(entity => entity.id)

  return {
    products: state.selectedProduct ? [state.selectedProduct.id] : [],
    customerGroups: filteredCustomerGroups,
    fromStops: state.selectedFromStop ? [state.selectedFromStop.id] : [],
    toStops: state.selectedToStop ? [state.selectedToStop.id] : [],
    startDate: formatProductsApiDate(startDate),
    modalities: rootGetters['selection/availableSelectedModalities'].map(entity => entity.id),
    productCollections: rootGetters['selection/availableSelectedProductCollections'].map(entity => entity.id),
    salesChannels: state.specialSaleSlug ? Vue.prototype.$container.config.productApiSalesChannelSpecialId : rootGetters.salesChannelIds,
    regions: rootGetters['selection/availableSelectedRegions'].map(entity => entity.id)
  }
}

const buildConfigurationCriteria = (filterCriteria: Object, state: ConfigurationState) => {
  return {
    ...filterCriteria,
    classes: state.selectedClass ? [state.selectedClass.id] : [],
    starValues: state.selectedStarValue ? [state.selectedStarValue.id] : [],
    zones: state.selectedZone ? [state.selectedZone.id] : []
  }
}

const buildCoveredPeriodCriteria = (filterCriteria: ConfigurationCriteria) => {
  return {
    ...filterCriteria,
    startDate: null
  }
}

const filter = async ({ commit, dispatch, state, rootState, getters, rootGetters }: ConfigurationActionContext) => {
  if (!state.selectedProduct) {
    throw new Error('Can not execute the configuration filter action if no product has been selected')
  }

  // Increment request count
  configurationFilterRequestCount++

  // Set current count
  let currentCount = configurationFilterRequestCount

  const productType = state.selectedProduct.__typename

  const filterCriteria = buildFilterCriteria(state, rootGetters, getters.startDate)
  const configurationCriteria = buildConfigurationCriteria(filterCriteria, state)

  const response = await Vue.$container.productsApiClient.getProductDetailFiltersAndConfigurations(
    productType,
    filterCriteria,
    configurationCriteria
  )

  // Ony commit/dispatch if 'newerRequests' is empty
  if (currentCount >= configurationFilterRequestCount) {
    commit(SET_PRODUCT_CONFIGURATIONS, response.data.configurations)
    commit(
      SET_AVAILABLE_FILTER_ENTITIES,
      {
        customerGroups: response.data.filters.customerGroups,
        classes: response.data.filters.classes,
        terms: response.data.filters.terms,
        zones: response.data.filters.zones,
        starValues: response.data.filters.starValues,
        stops: response.data.filters.stops
      }
    )
    await dispatch('filterSelections')
  }
}

/**
 *
 * Reset the selections that are no longer valid (selected entities that are no longer available)
 *
 * @param commit
 * @param state
 * @param rootState
 * @returns {Promise<void>}
 */
const filterSelections = async ({ commit, state, rootState }: ConfigurationActionContext) => {
  if (state.selectedProduct !== null) {
    if (rootState.selection.availableFilterEntities.products.find(({ id }) => state.selectedProduct && id === state.selectedProduct.id) === null) {
      commit(SET_SELECTED_PRODUCT, null)
    }
  }
  if (state.selectedCustomerGroup !== null) {
    if (state.availableFilterEntities.customerGroups.find(({ id }) => state.selectedCustomerGroup && id === state.selectedCustomerGroup.id) === null) {
      commit(SET_SELECTED_CUSTOMER_GROUP, null)
    }
  }
  if (state.selectedTerm !== null) {
    if (state.availableFilterEntities.terms.find(({ id }) => state.selectedTerm && id === state.selectedTerm.id) === null) {
      commit(SET_SELECTED_TERM, null)
    }
  }
  if (state.selectedFromStop !== null) {
    if (state.availableFilterEntities.stops.find(({ id }) => state.selectedFromStop && id === state.selectedFromStop.id) === null) {
      commit(SET_SELECTED_FROM_STOP, null)
    }
  }
  if (state.selectedToStop !== null) {
    if (state.availableFilterEntities.stops.find(({ id }) => state.selectedToStop && id === state.selectedToStop.id) === null) {
      commit(SET_SELECTED_TO_STOP, null)
    }
  }
  if (state.selectedZone !== null) {
    if (state.availableFilterEntities.zones.find(({ id }) => state.selectedZone && id === state.selectedZone.id) === null) {
      commit(SET_SELECTED_ZONE, null)
    }
  }
  if (state.selectedClass !== null) {
    if (state.availableFilterEntities.classes.find(({ id }) => state.selectedClass && id === state.selectedClass.id) === null) {
      commit(SET_SELECTED_CLASS, null)
    }
  }
  if (state.selectedStarValue !== null) {
    if (state.availableFilterEntities.starValues.find(({ id }) => state.selectedStarValue && id === state.selectedStarValue.id) === null) {
      commit(SET_SELECTED_STAR_VALUE, null)
    }
  }
}

const fetchCoveredPeriod = async ({ commit, state, rootState, getters, rootGetters }: ConfigurationActionContext) => {
  const filterCriteria = buildFilterCriteria(state, rootGetters, getters.startDate)
  const coveredPeriodCriteria = buildCoveredPeriodCriteria(filterCriteria)
  const response = await Vue.$container.productsApiClient.getCoveredPeriod(coveredPeriodCriteria)
  commit(SET_COVERED_PERIOD, response.data.coveredPeriod)
}

const applyPreselection = async ({ commit, state }: ConfigurationActionContext) => {
  if (state.preSelectedZoneId !== null) {
    const zone = state.availableFilterEntities.zones.filter(zone => zone.nrOfProductConfigurations > 0)
      .find(({ deeplinkId }) => deeplinkId === state.preSelectedZoneId)
    if (zone) {
      commit(SET_SELECTED_ZONE, zone)
    }
  }
  if (state.preSelectedZoneCode !== null) {
    const zone = state.availableFilterEntities.zones.filter(zone => zone.nrOfProductConfigurations > 0)
      .find(({ code }) => code === state.preSelectedZoneCode)
    if (zone) {
      commit(SET_SELECTED_ZONE, zone)
    }
  }
  if (state.preSelectedStarValueId !== null) {
    const starValue = state.availableFilterEntities.starValues
      .filter(starValue => starValue.nrOfProductConfigurations > 0)
      .find(({ deeplinkId }) => deeplinkId === state.preSelectedStarValueId)
    if (starValue) {
      commit(SET_SELECTED_STAR_VALUE, starValue)
    }
  }
  if (state.preSelectedClassId !== null) {
    const productClass = state.availableFilterEntities.classes
      .filter(productClass => productClass.nrOfProductConfigurations > 0)
      .find(({ deeplinkId }) => deeplinkId === state.preSelectedClassId)
    if (productClass) {
      commit(SET_SELECTED_CLASS, productClass)
    }
  }
  if (state.preSelectedFromStopId !== null) {
    const fromStop = state.availableFilterEntities.stops
      .filter(fromStop => fromStop.nrOfProductConfigurationsAsFromStop > 0)
      .find(({ deeplinkId }) => deeplinkId === state.preSelectedFromStopId)
    if (fromStop) {
      commit(SET_SELECTED_FROM_STOP, fromStop)
    }
  }
  if (state.preSelectedToStopId !== null) {
    const toStop = state.availableFilterEntities.stops
      .filter(toStop => toStop.nrOfProductConfigurationsAsToStop > 0)
      .find(({ deeplinkId }) => deeplinkId === state.preSelectedToStopId)
    if (toStop) {
      commit(SET_SELECTED_TO_STOP, toStop)
    }
  }
}

const setSessionId = ({ commit }: ConfigurationActionContext, sessionId: ?string) => {
  commit(SET_SESSION_ID, sessionId)
}

export default {
  filter,
  filterSelections,
  fetchCoveredPeriod,
  applyPreselection,
  setSessionId
}



// WEBPACK FOOTER //
// ./src/store/modules/configuration/actions.js